"use client";

import { cn } from "@/lib/utils";
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  expansionClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { hexToRgba } from "@/utils/utils";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
/** Vuesax bold arrow-circle-down icon (filled) */
const ArrowCircleDown = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.06 11.27L12.53 14.8C12.38 14.95 12.19 15.02 12 15.02C11.81 15.02 11.62 14.95 11.47 14.8L7.94 11.27C7.65 10.98 7.65 10.5 7.94 10.21C8.23 9.92 8.71 9.92 9 10.21L12 13.21L15 10.21C15.29 9.92 15.77 9.92 16.06 10.21C16.35 10.5 16.35 10.98 16.06 11.27Z"
      fill="currentColor"
    />
  </svg>
);

const ArrowDown = ({className}: {className?: string}) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M24.7994 12.4238L16.0892 21.1341L7.37891 12.4238" stroke="#101112" stroke-width="1.83692" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
);

interface FAQ {
  id: number;
  question: string;
  question_text_color?: string;
  answer: string;
  answer_text_color?: string;
  priority: number;
  is_active?: number;
  status?: number;
}

interface Category {
  id: number;
  name: string;
  text_color?: string;
  background_color?: string;
  active_text_color?: string;
  active_background_color?: string;
  order_index: number;
  status: boolean;
  faqs?: FAQ[];
}

const Faqs2 = ({ data }: { data: any }) => {
  const { data: sectionData } = data;
  const ref = useRef<HTMLElement>(null);

  const {
    title_1 = "Questions? We've Got",
    title_1_color = "rgba(255,255,255,0.8)",
    title_2 = "Answers",
    title_2_gradient_start_color = "rgba(255,255,255,1)",
    title_2_gradient_end_color = "rgba(2,167,182,1)",
    backvariant = "variant_1",
    gradient_color_1 = "rgba(0, 212, 235, 0.33)",
    gradient_color_2 = "rgba(0, 0, 0, 0.66)",
    gradient_color_3 = "rgba(0, 0, 0, 0.66)",
    gradient_color_4 = "rgba(83, 69, 195, 0.46)",
    background_color = "#000000",
    top_arrow_icon = "",
    top_arrow_icon_alt = "Collapse",
    bottom_arrow_icon = "",
    bottom_arrow_icon_alt = "Expand",
    enable_animation = false,
    categories = [],
    layout_variant = "variant_1",
  } = sectionData || {};

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const isV2 = layout_variant === "variant_2";

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_3":
        return background_color;
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
    }
  };

  // Sort categories by order_index and filter by status
  const activeCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return [...categories]
      .filter((cat: Category) => cat.status)
      .sort((a: Category, b: Category) => a.order_index - b.order_index);
  }, [categories]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    activeCategories.length > 0 ? activeCategories[0].id : null,
  );

  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  // Get FAQs from selected category and sort by priority
  const filteredFaqs = useMemo(() => {
    if (selectedCategoryId === null) return [];
    const selectedCategory = activeCategories.find(
      (cat) => cat.id === selectedCategoryId,
    );
    if (
      !selectedCategory ||
      !selectedCategory.faqs ||
      selectedCategory.faqs.length === 0
    ) {
      return [];
    }
    return [...selectedCategory.faqs]
      .filter((faq: FAQ) => faq.is_active !== 0 && faq.status !== 0)
      .sort((a: FAQ, b: FAQ) => a.priority - b.priority);
  }, [activeCategories, selectedCategoryId]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "faqs_section");
  //   }
  // }, []);

  // Reset expanded FAQ when category changes
  useEffect(() => {
    setExpandedFaqId(null);
  }, [selectedCategoryId]);

  // Handler for category selection with tracking
  const handleCategoryClick = (categoryId: number) => {
    const category = activeCategories.find((cat) => cat.id === categoryId);
    if (category) {
      tabSwitched({
        section: "faqs_section",
        component: "category_tab",
        label: category.name,
      });
      setSelectedCategoryId(categoryId);
    }
  };

  // Handler for FAQ accordion value change with tracking
  const handleAccordionValueChange = (value: string) => {
    const faqId = value === "" ? null : value;
    const previousFaqId = expandedFaqId;

    if (faqId && faqId !== previousFaqId) {
      const faqIndex = filteredFaqs.findIndex((f) => f.id.toString() === faqId);
      const faq = filteredFaqs[faqIndex];
      const selectedCategory = activeCategories.find(
        (cat) => cat.id === selectedCategoryId,
      );
      if (faq && selectedCategory) {
        expansionClick({
          section: "faqs_section",
          component: "faq_item",
          label: faq.question,
          index: String(faqIndex),
          entity_name: faq.id.toString(),
          parent_entity: selectedCategory.name,
        });
      }
    } else if (!faqId && previousFaqId) {
      const faqIndex = filteredFaqs.findIndex(
        (f) => f.id.toString() === previousFaqId,
      );
      const faq = filteredFaqs[faqIndex];
      const selectedCategory = activeCategories.find(
        (cat) => cat.id === selectedCategoryId,
      );
      if (faq && selectedCategory) {
        expansionClick({
          section: "faqs_section",
          component: "faq_item",
          label: faq.question,
          index: String(faqIndex),
          entity_name: faq.id.toString(),
          parent_entity: selectedCategory.name,
        });
      }
    }

    setExpandedFaqId(faqId);
  };

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ background: getGradientBackground() }}
    >
      {/* Content Container */}
      <div
        className={cn(
          "relative z-10 mx-auto",
          // xs: stacked vertical layout
          "flex flex-col gap-[24px] px-[24px] py-[40px]",
          // sm (1080px): horizontal layout matching Figma
          `sm:flex-row sm:items-start sm:gap-[40px] sm:p-[60px] sm:max-w-[1080px]`,
          `md:max-w-[1230px] md:p-[60px] md:gap-[80px]`,
          `lg:max-w-[1430px] lg:px-[80px] lg:py-[60px] lg:gap-[120px]`,
          `xl:max-w-[1750px] xl:px-[140px] xl:py-[80px]`,
        )}
      >
        {/* Left Column - Title */}
        <div className={`flex flex-col gap-[4px] sm:gap-[2px] shrink-0 max-w-full ${isV2 ? "sm:max-w-[35%]" : "sm:max-w-[30%]"} ${isV2 ? "text-center sm:text-left" : ""}`}>
          {title_1 && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className={cn(
                "font-semibold line-clamp-3",
                "text-[24px] leading-normal",
                "sm:text-[28px] md:text-[30px]",
                isV2 ? "hidden" : "",
              )}
              style={{ color: title_1_color, fontFamily: isV2 ? "var(--font-poppins)" : "var(--font-google-sans)" }}
              title={title_1}
            >
              {title_1}
            </p>
            </RevealOnScroll>
          )}
          {title_2 && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className={cn(
                "line-clamp-3",
                isV2
                  ? cn(
                      // Figma "Typography System · 4 Breakpoints": Poppins SemiBold,
                      // 28px (mobile) → 34px (web, 1080+), letter-spacing 0, normal
                      // line-height. Wraps to two lines in the left column like the design.
                      "font-600 leading-normal",
                      "text-[28px] sm:text-[34px]",
                    )
                  : cn(
                      "font-bold leading-120 tracking-[1.2px]",
                      "sm:text-[44px] sm:tracking-[1.32px]",
                      "md:text-[54px] md:tracking-[1.62px]",
                      "lg:text-[64px] lg:tracking-[1.92px]",
                    ),
              )}
              style={{
                background: `radial-gradient(ellipse at top left, ${title_2_gradient_start_color} 0%, ${title_2_gradient_end_color} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontFamily: isV2 ? "var(--font-poppins)" : "var(--font-merriweather)",
              }}
              title={title_2}
            >
              {title_2}
            </p>
            </RevealOnScroll>
          )}
        </div>

        {/* Right Column - FAQ Card */}
        <RevealOnScroll y={0} x={300} className="w-full" enableAnimation={isAnimationEnabled}>
        <div
          className={cn(
            "flex-1 min-w-0 flex flex-col gap-[24px] rounded-[20px] overflow-hidden",
            isV2 ? "" : "bg-[rgba(255,255,255,0.14)] sm:bg-[rgba(255,255,255,0.08)]" ,
            isV2 ? "px-[16px]" : "p-[16px]",
            isV2 ? "px-[16px]": "sm:p-[30px]",
          )}
        >
          {/* Category Tabs */}
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
          {activeCategories.length > 0 && (
            <div
              className={cn(
                "flex items-center gap-[10px] sm:gap-[12px]",
                "overflow-x-auto hide-scrollbar",
                "sm:flex-wrap",
              )}
            >
              {activeCategories.map((category) => (
                <button
                  key={category.id}
                  data-cta-name="faq_category"
                  className={cn(
                    "cursor-pointer flex items-center justify-center rounded-[40px] shrink-0 transition-all duration-200",
                    "text-[14px] font-semibold leading-[1.49] px-[16px] py-[10px]",
                    "sm:text-[13px]",
                    "md:text-[14px]",
                    "lg:text-[16px]",
                    selectedCategoryId === category.id
                      ? "border border-transparent"
                      : "border-[0.6px]",
                  )}
                  style={{
                    backgroundColor:
                      selectedCategoryId === category.id
                        ? category.active_background_color || category.background_color
                        : category.background_color || "transparent",
                    color:
                      selectedCategoryId === category.id
                        ? category.active_text_color || category.text_color
                        : category.text_color,
                    borderColor:
                      selectedCategoryId !== category.id
                        ? category.text_color
                        : "transparent",
                    fontFamily: "var(--font-google-sans)",
                  }}
                  onClick={() => handleCategoryClick(category.id)}
                  title={category.name}
                >
                  {category.name.slice(0, 50)}
                </button>
              ))}
            </div>
          )}
          </RevealOnScroll>

          {/* FAQ Accordion */}
          <Accordion
            type="single"
            collapsible
            value={expandedFaqId || ""}
            onValueChange={handleAccordionValueChange}
            className={cn(
              "flex flex-col",
              isV2 ? "bg-white p-[24px] rounded-lg" : ""
            )}
          >
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <RevealOnScroll
                  y={60}
                  key={faq.id}
                  duration={1500}
                  enableAnimation={isAnimationEnabled}
                >
                <AccordionItem
                  key={faq.id}
                  value={faq.id.toString()}
                  className={cn(
                    "border-b border-[rgba(255,255,255,0.1)] last:border-b-0",
                    "py-[16px]",
                    "sm:py-[20px]",
                    "lg:py-[24px]",
                  )}
                >
                  <AccordionTrigger
                    adminIcon={false}
                    className="cursor-pointer gap-[10px] sm:gap-[30px] lg:gap-[40px]"
                  >
                    <div
                      data-cta
                      data-cta-name="faq_expand"
                      className={cn(
                        "flex-1 font-medium leading-140 text-left line-clamp-4",
                        "text-[14px]",
                        "sm:text-[16px]",
                        "md:text-[18px]",
                        "lg:text-[20px]",
                      )}
                      style={{ color: faq.question_text_color, fontFamily: isV2 ? "var(--font-poppins)" : "var(--font-google-sans)" }}
                      title={faq.question}
                    >
                      {faq.question}
                    </div>
                    {/* Arrow Icons */}
                    {top_arrow_icon || bottom_arrow_icon ? (
                      <>
                        {/* Top arrow (collapse) - visible when open */}
                        {top_arrow_icon && (
                          <img
                            src={top_arrow_icon}
                            alt={top_arrow_icon_alt}
                            className="shrink-0 size-[24px] hidden group-data-[state=open]:block"
                          />
                        )}
                        {/* Bottom arrow (expand) - visible when closed */}
                        {bottom_arrow_icon && (
                          <img
                            src={bottom_arrow_icon}
                            alt={bottom_arrow_icon_alt}
                            className="shrink-0 size-[24px] group-data-[state=open]:hidden"
                          />
                        )}
                      </>
                    ) : (
                      <>
                      {
                        isV2 ? 
                        <ArrowDown 
                          className={cn(
                            "shrink-0 size-[24px] text-black",
                            "transition-transform duration-200",
                            "group-data-[state=open]:rotate-180",
                          )}
                        />
                        :
                        <ArrowCircleDown
                          className={cn(
                            `shrink-0 size-[24px] ${isV2 ? "text-black" : "text-white"}`,
                            "transition-transform duration-200",
                            "group-data-[state=open]:rotate-180",
                          )}
                        />
                      }
                      </>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className={cn("pt-[16px]", "sm:pt-[20px]")}>
                    <p
                      className={cn(
                        "leading-140 line-clamp-4",
                        "text-[12px]",
                        "sm:text-[12px]",
                        "md:text-[14px]",
                        "lg:text-[16px]",
                      )}
                      style={{ color: faq.answer_text_color, fontFamily: isV2 ? "var(--font-poppins)" : "var(--font-google-sans)" }}
                      title={faq.answer}
                    >
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
                {
                  isV2 && index !== filteredFaqs.length - 1 ? <hr style={{ borderColor: "#C5C5C5" }} /> : null
                }
                </RevealOnScroll>
              ))
            ) : (
              <RevealOnScroll enableAnimation={isAnimationEnabled}>
              <div className="text-center py-8 text-[rgba(255,255,255,0.5)]">
                No FAQs available for this category.
              </div>
              </RevealOnScroll>
            )}
          </Accordion>
        </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

export default Faqs2;
